import { useEffect, useState } from "react";
import "@/vault-template/vault-template.css";
import ReceptionistWidget from "@/vault-template/components/ReceptionistWidget";
import {
  SiteConfigProvider,
  EMPTY_VAULT_CONTENT,
} from "@/vault-template/SiteConfigContext";
import type { SiteConfig } from "@/vault-template/config";
import { siteConfig } from "@/vault-template/config";
import type { FooterModel } from "@shared/footerModel";

// Digital Receptionist for the marketing /results page — the SAME widget the
// root sales page ("/") mounts, wired to the sales owner's slug so answers
// come from the owner's approved knowledgebase and lead capture flows into
// their CRM (formSource "receptionist").
//
// Owner resolution: /api/sales-footer already resolves the sales owner and
// exposes homeHref = "/p/<slug>" — the slug is derived from it, so this stays
// in lockstep with whoever the root sales page belongs to. The owner's live
// widget config (/api/ic/widget/<slug>) supplies the receptionist's display
// name and accent color. If either fetch fails, nothing renders (the page
// simply has no chat bubble — no broken preview-mode widget).
//
// The widget snapshots --theme-* vars from '[data-testid="vault-template-root"]'
// (see its portal comment); /results has no vault root, so a zero-size stand-in
// div carries the owner's accent for the snapshot.

const HEX = /^#[0-9a-fA-F]{6}$/;

export default function SalesReceptionist() {
  const [slug, setSlug] = useState<string | null>(null);
  const [receptionistName, setReceptionistName] = useState("");
  const [accent, setAccent] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const footerRes = await fetch("/api/sales-footer");
        if (!footerRes.ok) return;
        const footer: FooterModel | null = await footerRes.json();
        const m = footer?.homeHref?.match(/^\/p\/([^/?#]+)/);
        if (!m) return;
        const ownerSlug = m[1];
        const widgetRes = await fetch(`/api/ic/widget/${encodeURIComponent(ownerSlug)}`, {
          credentials: "omit",
        });
        if (!widgetRes.ok) return;
        const data: any = await widgetRes.json();
        if (!alive) return;
        const w = data?.widget ?? {};
        setReceptionistName(typeof w.receptionistName === "string" ? w.receptionistName : "");
        setAccent(HEX.test(w.accentColor ?? "") ? w.accentColor : null);
        setSlug(ownerSlug);
        setReady(true);
      } catch {
        // No chat bubble on failure — never a broken preview-mode widget.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!ready || !slug) return null;

  return (
    <SiteConfigProvider
      config={siteConfig as SiteConfig}
      content={{
        ...EMPTY_VAULT_CONTENT,
        receptionist: { ...EMPTY_VAULT_CONTENT.receptionist, name: receptionistName },
      }}
    >
      {/* Zero-size stand-in for the vault root: the widget reads --theme-accent
          from this element to color its portaled launcher/panel. */}
      <div
        data-testid="vault-template-root"
        aria-hidden="true"
        className="hidden"
        style={accent ? ({ ["--theme-accent" as string]: accent } as React.CSSProperties) : undefined}
      />
      <ReceptionistWidget slug={slug} />
    </SiteConfigProvider>
  );
}
